import * as XLSX from 'xlsx';
import {
  VISITAS_AGENDA_DIA_KEYS,
  VisitasAgendaDiaKey,
  VisitasAgendaDias,
  VisitasAgendaFila,
  emptyVisitasAgendaDias,
} from '../types/visitasAgenda';

export interface VisitasAgendaImportRowError {
  fila: number;
  mensaje: string;
}

export interface VisitasAgendaImportResult {
  filas: Omit<VisitasAgendaFila, 'idVisitasAgenda'>[];
  errores: VisitasAgendaImportRowError[];
}

export interface VisitasAgendaMergeResult {
  filas: VisitasAgendaFila[];
  agregados: number;
  actualizados: number;
  sinCambios: number;
}

interface AgendaColumnIndices {
  idxCuenta: number;
  idxRazon: number;
  idxDir: number;
  idxCodigoDir: number;
  idxFrecuencia: number;
  diaIndices: Partial<Record<VisitasAgendaDiaKey, number>>;
}

interface AgendaSheetLayout {
  sheetName: string;
  headerRowIndex: number;
  columns: AgendaColumnIndices;
}

const FRECUENCIA_LABELS: Record<VisitasAgendaDiaKey, string> = {
  lun: 'LUN',
  mar: 'MAR',
  mier: 'MIE',
  jue: 'JUE',
  vie: 'VIE',
  sab: 'SAB',
  dom: 'DOM',
};

const DIA_HEADER_ALIASES: Record<VisitasAgendaDiaKey, string[]> = {
  lun: ['lunes', 'lun'],
  mar: ['martes', 'mar'],
  mier: ['miercoles', 'mier', 'mie'],
  jue: ['jueves', 'jue'],
  vie: ['viernes', 'vie'],
  sab: ['sabado', 'sab'],
  dom: ['domingo', 'dom'],
};

const stripAccents = (value: string): string =>
  value.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

const normalizeHeader = (value: unknown): string =>
  stripAccents(String(value ?? '').trim().toLowerCase());

export const formatDiasToFrecuencia = (dias: VisitasAgendaDias): string => {
  const active = VISITAS_AGENDA_DIA_KEYS.filter((k) => dias[k]);
  if (active.length === 0) return '';
  if (active.length === VISITAS_AGENDA_DIA_KEYS.length) return 'TODOS';
  return active.map((k) => FRECUENCIA_LABELS[k]).join('/');
};

const parseDiasFromFrecuencia = (raw: string): VisitasAgendaDias => {
  const dias = emptyVisitasAgendaDias();
  const text = stripAccents(raw.trim()).toUpperCase();

  if (!text) return dias;

  if (text.includes('TODOS')) {
    VISITAS_AGENDA_DIA_KEYS.forEach((k) => {
      dias[k] = true;
    });
    return dias;
  }

  const tokens = text.split(/[/,\-|]+/).map((t) => t.trim()).filter(Boolean);

  const matchToken = (token: string): VisitasAgendaDiaKey | null => {
    if (token.startsWith('LUN')) return 'lun';
    if (token.startsWith('MAR')) return 'mar';
    if (token.startsWith('MIE')) return 'mier';
    if (token.startsWith('JUE')) return 'jue';
    if (token.startsWith('VIE')) return 'vie';
    if (token.startsWith('SAB')) return 'sab';
    if (token.startsWith('DOM')) return 'dom';
    return null;
  };

  tokens.forEach((token) => {
    const key = matchToken(token);
    if (key) dias[key] = true;
  });

  return dias;
};

const parseBoolCell = (value: unknown): boolean => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  const text = stripAccents(String(value ?? '').trim().toLowerCase());
  if (!text) return false;
  return ['1', 'si', 'sí', 'true', 'x', 'v', 'ok', 'yes', 'y'].includes(text);
};

const isNroCuentaHeader = (header: string): boolean => {
  if (!header) return false;
  if (
    header.includes('tipoformulario') ||
    header.includes('comprobante') ||
    header.includes('formulario') ||
    header.includes('tipo cliente')
  ) {
    return false;
  }
  if (/nro\s*\.?\s*cuenta/.test(header)) return true;
  if (/nro\s*\.?\s*cliente/.test(header)) return true;
  if (header.includes('nro') && header.includes('cuenta')) return true;
  if (header.includes('nro') && header.includes('cliente')) return true;
  return header === 'cuenta' || header.startsWith('cuenta ');
};

const isFrecuenciaHeader = (header: string): boolean => header.includes('frecuencia');

const isCodigoDireccionHeader = (header: string): boolean => {
  if (!header) return false;
  if (header.includes('codigo') && header.includes('direccion')) return true;
  return header === 'cod direccion' || header === 'cod. direccion' || header === 'codigo dir';
};

const resolveDiaIndices = (headers: string[]): Partial<Record<VisitasAgendaDiaKey, number>> => {
  const result: Partial<Record<VisitasAgendaDiaKey, number>> = {};
  VISITAS_AGENDA_DIA_KEYS.forEach((dia) => {
    const aliases = DIA_HEADER_ALIASES[dia];
    const idx = headers.findIndex((h) => aliases.some((a) => h === a));
    if (idx >= 0) result[dia] = idx;
  });
  return result;
};

const resolveColumnIndices = (headers: string[]): AgendaColumnIndices | null => {
  const idxCuenta = headers.findIndex(isNroCuentaHeader);
  const idxFrecuencia = headers.findIndex(isFrecuenciaHeader);
  const diaIndices = resolveDiaIndices(headers);
  const hasDiaColumns = Object.keys(diaIndices).length > 0;

  if (idxCuenta < 0) return null;
  if (idxFrecuencia < 0 && !hasDiaColumns) return null;

  const idxRazon = headers.findIndex(
    (h) =>
      (h.includes('razon') && h.includes('social')) ||
      h === 'nombre' ||
      h.startsWith('nombre ') ||
      h === 'cliente' ||
      h.includes('nombre display'),
  );
  const idxCodigoDir = headers.findIndex(isCodigoDireccionHeader);
  const idxDir = headers.findIndex(
    (h) =>
      !isCodigoDireccionHeader(h) &&
      ((h.includes('direccion') && h.includes('envio')) ||
        (h.includes('direccion') &&
          !h.includes('facturacion') &&
          !h.includes('ciudad') &&
          !h.includes('resumen') &&
          !h.includes('codigo'))),
  );

  return {
    idxCuenta,
    idxRazon,
    idxDir,
    idxCodigoDir,
    idxFrecuencia,
    diaIndices,
  };
};

const findAgendaSheetLayout = (workbook: XLSX.WorkBook): AgendaSheetLayout | null => {
  for (const sheetName of workbook.SheetNames) {
    const sheet = workbook.Sheets[sheetName];
    if (!sheet) continue;

    const rows = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
      header: 1,
      defval: '',
      raw: false,
    }) as unknown[][];

    for (let r = 0; r < Math.min(rows.length, 25); r += 1) {
      const headers = (rows[r] as unknown[]).map(normalizeHeader);
      const columns = resolveColumnIndices(headers);
      if (columns) {
        return { sheetName, headerRowIndex: r, columns };
      }
    }
  }
  return null;
};

const parseNroCliente = (value: unknown): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.round(value);
  }
  const cleaned = String(value ?? '')
    .trim()
    .replace(/\s/g, '')
    .replace(/,/g, '');
  const n = Number(cleaned);
  return Number.isFinite(n) ? Math.round(n) : NaN;
};

const cellText = (value: unknown): string => {
  if (value == null) return '';
  return String(value).trim();
};

const agendaRowKey = (nroCliente: number, codigoDireccion?: string | null): string =>
  `${nroCliente}|${(codigoDireccion ?? '').trim()}`;

const diasEqual = (a: VisitasAgendaDias, b: VisitasAgendaDias): boolean =>
  VISITAS_AGENDA_DIA_KEYS.every((k) => Boolean(a[k]) === Boolean(b[k]));

export const mergeVisitasAgendaFilas = (
  existing: VisitasAgendaFila[],
  imported: Omit<VisitasAgendaFila, 'idVisitasAgenda'>[],
  newId: () => string,
): VisitasAgendaMergeResult => {
  const byKey = new Map<string, VisitasAgendaFila>();
  existing.forEach((f) => {
    byKey.set(agendaRowKey(f.nroCliente, f.codigoDireccion), { ...f, dias: { ...f.dias } });
  });

  const resolveTargetKey = (
    nroCliente: number,
    codigoDireccion?: string | null,
  ): string | null => {
    const exact = agendaRowKey(nroCliente, codigoDireccion);
    if (byKey.has(exact)) return exact;

    // Import legacy sin código: si hay una sola fila del cliente, actualizarla.
    if (!(codigoDireccion ?? '').trim()) {
      const sameCliente = Array.from(byKey.values()).filter((f) => f.nroCliente === nroCliente);
      if (sameCliente.length === 1) {
        return agendaRowKey(sameCliente[0].nroCliente, sameCliente[0].codigoDireccion);
      }
    }
    return null;
  };

  let agregados = 0;
  let actualizados = 0;
  let sinCambios = 0;

  imported.forEach((imp) => {
    const targetKey = resolveTargetKey(imp.nroCliente, imp.codigoDireccion);
    const dias = { ...emptyVisitasAgendaDias(), ...imp.dias };

    if (!targetKey) {
      byKey.set(agendaRowKey(imp.nroCliente, imp.codigoDireccion), {
        ...imp,
        idVisitasAgenda: newId(),
        dias,
        codigoDireccion: imp.codigoDireccion?.trim() || undefined,
      });
      agregados += 1;
      return;
    }

    const current = byKey.get(targetKey);
    if (!current) {
      agregados += 1;
      return;
    }

    const nextCodigo =
      imp.codigoDireccion?.trim() || current.codigoDireccion;
    const sameDias = diasEqual(current.dias, dias);
    const sameNombre =
      !imp.nombreDisplay?.trim() ||
      current.nombreDisplay.trim() === imp.nombreDisplay.trim();
    const sameCod = (current.codigoDireccion ?? '').trim() === (nextCodigo ?? '').trim();

    if (sameDias && sameNombre && sameCod) {
      sinCambios += 1;
      return;
    }

    const nextKey = agendaRowKey(current.nroCliente, nextCodigo);
    if (nextKey !== targetKey) {
      byKey.delete(targetKey);
    }
    byKey.set(nextKey, {
      ...current,
      nombreDisplay: imp.nombreDisplay?.trim() || current.nombreDisplay,
      codigoDireccion: nextCodigo,
      dias,
    });
    actualizados += 1;
  });

  return {
    filas: Array.from(byKey.values()),
    agregados,
    actualizados,
    sinCambios,
  };
};

export const exportVisitasAgendaExcel = (filas: VisitasAgendaFila[], fileName?: string): void => {
  const rows = filas.map((f) => {
    const dias = { ...emptyVisitasAgendaDias(), ...f.dias };
    return {
      'Nro Cuenta': f.nroCliente,
      Nombre: f.nombreDisplay,
      'Codigo Direccion': f.codigoDireccion ?? '',
      FRECUENCIA: formatDiasToFrecuencia(dias),
      Lunes: dias.lun ? 1 : 0,
      Martes: dias.mar ? 1 : 0,
      Miercoles: dias.mier ? 1 : 0,
      Jueves: dias.jue ? 1 : 0,
      Viernes: dias.vie ? 1 : 0,
      Sabado: dias.sab ? 1 : 0,
      Domingo: dias.dom ? 1 : 0,
    };
  });

  const worksheet = XLSX.utils.json_to_sheet(rows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Agenda');
  const name =
    fileName ??
    `visitas-agenda_${new Date().toISOString().slice(0, 10)}.xlsx`;
  XLSX.writeFile(workbook, name);
};

export const parseVisitasAgendaExcel = async (file: File): Promise<VisitasAgendaImportResult> => {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array', cellDates: false });

  if (!workbook.SheetNames.length) {
    return { filas: [], errores: [{ fila: 0, mensaje: 'El archivo no tiene hojas.' }] };
  }

  const layout = findAgendaSheetLayout(workbook);
  if (!layout) {
    const hojas = workbook.SheetNames.join(', ');
    return {
      filas: [],
      errores: [
        {
          fila: 0,
          mensaje: `No se encontró una hoja con columnas "Nro Cuenta" y "FRECUENCIA" (o días). Hojas: ${hojas}. Use Exportar Excel de esta pantalla o un listado con agenda.`,
        },
      ],
    };
  }

  const sheet = workbook.Sheets[layout.sheetName];
  const rows = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
    header: 1,
    defval: '',
    raw: false,
  }) as unknown[][];

  const { headerRowIndex, columns } = layout;
  const { idxCuenta, idxRazon, idxDir, idxCodigoDir, idxFrecuencia, diaIndices } = columns;
  const hasDiaColumns = Object.keys(diaIndices).length > 0;

  const filas: Omit<VisitasAgendaFila, 'idVisitasAgenda'>[] = [];
  const errores: VisitasAgendaImportRowError[] = [];
  const seen = new Set<string>();

  for (let i = headerRowIndex + 1; i < rows.length; i += 1) {
    const row = rows[i] as unknown[];
    if (!row || row.every((c) => cellText(c) === '')) continue;

    const nroCliente = parseNroCliente(row[idxCuenta]);
    if (!Number.isFinite(nroCliente) || nroCliente <= 0) continue;

    const razon = idxRazon >= 0 ? cellText(row[idxRazon]) : '';
    const direccion = idxDir >= 0 ? cellText(row[idxDir]) : '';
    const codigoDireccion = idxCodigoDir >= 0 ? cellText(row[idxCodigoDir]) || undefined : undefined;
    const frecuencia = idxFrecuencia >= 0 ? cellText(row[idxFrecuencia]) : '';

    let dias = emptyVisitasAgendaDias();
    if (hasDiaColumns) {
      VISITAS_AGENDA_DIA_KEYS.forEach((dia) => {
        const idx = diaIndices[dia];
        if (idx != null) dias[dia] = parseBoolCell(row[idx]);
      });
    }
    if (!Object.values(dias).some(Boolean) && frecuencia) {
      dias = parseDiasFromFrecuencia(frecuencia);
    }

    if (!Object.values(dias).some(Boolean)) {
      if (frecuencia || hasDiaColumns) {
        errores.push({
          fila: i + 1,
          mensaje: frecuencia
            ? `Frecuencia no reconocida: "${frecuencia}"`
            : 'Sin días marcados',
        });
      }
      continue;
    }

    const key = agendaRowKey(nroCliente, codigoDireccion);
    if (seen.has(key)) {
      errores.push({
        fila: i + 1,
        mensaje: `Fila duplicada para cliente ${nroCliente}${codigoDireccion ? ` / ${codigoDireccion}` : ''}`,
      });
      continue;
    }
    seen.add(key);

    const nombreDisplay =
      razon ||
      [direccion, codigoDireccion].filter(Boolean).join(' — ') ||
      `Cliente ${nroCliente}`;

    filas.push({
      nroCliente,
      codigoDireccion,
      nombreDisplay,
      dias,
    });
  }

  if (filas.length === 0 && errores.length === 0) {
    return {
      filas: [],
      errores: [
        {
          fila: 0,
          mensaje: `Hoja "${layout.sheetName}": no hay filas válidas. Revise Nro Cuenta y FRECUENCIA/días.`,
        },
      ],
    };
  }

  return { filas, errores };
};
